import React, { useState, useEffect } from 'react';

const STORAGE_KEY = 'zt_onboarded_v1';

const GOLD = '#D4A417';
const PANEL_BG = '#1e2235';
const SURFACE = '#13161e';
const BORDER = '#252a38';
const TEXT = '#f3f4f6';
const SUBTEXT = '#9ca3af';

const steps = [
  {
    icon: '⚡',
    title: 'Welcome to AECWorks AI Tools',
    subtitle: 'AEC document intelligence platform',
    body: 'AW AI Tools analyzes your construction specifications, submittals, shop drawings, and RFIs — giving you the kind of detailed compliance review that normally takes hours, in minutes.',
    tip: null,
  },
  {
    icon: '🔀',
    title: 'Two Ways to Work',
    subtitle: 'Manager mode vs. Traditional mode',
    body: 'Use the toggle in the top bar to switch between modes. Traditional mode gives you direct tab-by-tab access to every tool. Manager mode lets you describe what you need in plain language and AW Manager routes your files automatically.',
    tip: '💡 Manager mode is recommended — attach files, type what you want, and AW Manager handles the rest.',
  },
  {
    icon: '🤖',
    title: 'AW Manager',
    subtitle: 'Your AI orchestration agent',
    body: 'The gold pulsing dot on the right is AW Manager. Attach documents, describe what you need, and it routes to the right tool automatically. It can also chain multiple tools together — Spec Check → Draft RFIs → Submittal Letter — in one session.',
    tip: '💡 Try: "Run a spec check on these files" or ask a direct AEC question about your uploaded documents.',
  },
  {
    icon: '📋',
    title: 'Spec Check & Core Tools',
    subtitle: 'Full suite of AEC compliance tools',
    body: 'Spec Check cross-references every requirement and flags non-compliant, missing, or partial items with a compliance score. Package Check reviews shop drawing packages. Design vs. Shop compares design intent against contractor submissions. Track Changes gaps master vs. outbound specs.',
    tip: '💡 Upload multiple spec files at once — AW AI Tools cross-references them all against a single submittal.',
  },
  {
    icon: '🔗',
    title: 'Procore & Autodesk Integration',
    subtitle: 'Load files directly from your project folders',
    body: 'Connect your Procore or Autodesk Construction Cloud account in the Integrations tab. Once connected, a Platform Files panel appears on every tool tab — browse your projects and load files directly into any upload slot without manual downloads.',
    tip: '💡 Go to Integrations → Connect Procore or Autodesk → then use Platform Files on any tool tab.',
  },
  {
    icon: '📂',
    title: 'Recent Documents',
    subtitle: 'Every upload cached for 48 hours',
    body: 'Every file you upload is automatically cached for 48 hours. The Recent Documents panel on every tool tab lets you reload any cached file into any upload slot instantly — no re-uploading needed across sessions.',
    tip: null,
  },
  {
    icon: '🏢',
    title: 'Your Firm\'s Intelligence',
    subtitle: 'Customized for your practice',
    body: 'AW AI Tools applies your firm\'s House Rules — custom compliance checks for your standards, preferred manufacturers, and code requirements — automatically on every analysis. Configure them in Settings → House Rules.',
    tip: '💡 Use Settings → Firm Profile to set your default codes, preferred manufacturers, and report terminology.',
  },
];

export default function OnboardingWalkthrough({ onComplete }) {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      setTimeout(() => setVisible(true), 800);
    }
  }, []);

  const dismiss = () => {
    setExiting(true);
    setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, '1');
      setVisible(false);
      setExiting(false);
      onComplete?.();
    }, 300);
  };

  const next = () => {
    if (step < steps.length - 1) {
      setStep(s => s + 1);
    } else {
      dismiss();
    }
  };

  const prev = () => {
    if (step > 0) setStep(s => s - 1);
  };

  if (!visible) return null;

  const current = steps[step];
  const isLast = step === steps.length - 1;
  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.75)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      opacity: exiting ? 0 : 1,
      transition: 'opacity 0.3s ease',
    }}>
      <div style={{
        background: PANEL_BG,
        border: `1px solid ${BORDER}`,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '480px',
        overflow: 'hidden',
        boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
        transform: exiting ? 'scale(0.96)' : 'scale(1)',
        transition: 'transform 0.3s ease',
      }}>

        {/* Gold top bar */}
        <div style={{ height: '4px', background: GOLD, width: '100%' }} />

        {/* Progress bar */}
        <div style={{ height: '2px', background: BORDER }}>
          <div style={{
            height: '100%',
            background: GOLD,
            width: `${progress}%`,
            transition: 'width 0.4s ease',
            opacity: 0.5,
          }} />
        </div>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px 0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: GOLD,
              animation: 'ztPulse 1.6s ease-in-out infinite',
              display: 'inline-block',
            }} />
            <span style={{ fontSize: '11px', color: SUBTEXT, fontFamily: 'sans-serif' }}>
              AW AI Tools — Getting Started ({step + 1} of {steps.length})
            </span>
          </div>
          <button
            onClick={dismiss}
            style={{
              background: 'transparent',
              border: 'none',
              color: SUBTEXT,
              cursor: 'pointer',
              fontSize: '18px',
              lineHeight: 1,
              padding: '4px',
              fontFamily: 'sans-serif',
            }}
            title="Skip tour"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px 28px 20px' }}>
          {/* Icon */}
          <div style={{
            width: '56px', height: '56px',
            borderRadius: '14px',
            background: `rgba(212,164,23,0.12)`,
            border: `1px solid rgba(212,164,23,0.25)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '26px',
            marginBottom: '18px',
          }}>
            {current.icon}
          </div>

          {/* Title */}
          <div style={{
            fontSize: '11px',
            color: GOLD,
            fontFamily: 'sans-serif',
            fontWeight: '600',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: '6px',
          }}>
            {current.subtitle}
          </div>
          <div style={{
            fontSize: '22px',
            fontWeight: '700',
            color: TEXT,
            fontFamily: 'sans-serif',
            lineHeight: 1.2,
            marginBottom: '14px',
          }}>
            {current.title}
          </div>

          {/* Body */}
          <div style={{
            fontSize: '14px',
            color: SUBTEXT,
            fontFamily: 'sans-serif',
            lineHeight: 1.6,
            marginBottom: current.tip ? '14px' : '0',
          }}>
            {current.body}
          </div>

          {/* Tip */}
          {current.tip && (
            <div style={{
              background: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: '8px',
              padding: '10px 14px',
              fontSize: '13px',
              color: '#d1d5db',
              fontFamily: 'sans-serif',
              lineHeight: 1.5,
            }}>
              {current.tip}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 28px 24px',
          borderTop: `1px solid ${BORDER}`,
        }}>
          {/* Step dots */}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                style={{
                  width: i === step ? '20px' : '6px',
                  height: '6px',
                  borderRadius: '3px',
                  background: i === step ? GOLD : BORDER,
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            {step > 0 && (
              <button
                onClick={prev}
                style={{
                  padding: '9px 18px',
                  borderRadius: '8px',
                  border: `1px solid ${BORDER}`,
                  background: 'transparent',
                  color: SUBTEXT,
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: 'sans-serif',
                }}
              >
                Back
              </button>
            )}
            <button
              onClick={next}
              style={{
                padding: '9px 22px',
                borderRadius: '8px',
                border: 'none',
                background: GOLD,
                color: '#13161e',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                fontFamily: 'sans-serif',
              }}
            >
              {isLast ? 'Get Started' : 'Next →'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ztPulse {
          0%   { transform: scale(1);   opacity: 1; }
          50%  { transform: scale(1.6); opacity: 0.4; }
          100% { transform: scale(1);   opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// Helper to reset onboarding (call from console: window.resetZTOnboarding())
if (typeof window !== 'undefined') {
  window.resetZTOnboarding = () => {
    localStorage.removeItem('zt_onboarded_v1');
    window.location.reload();
  };
}